/**
 * SynthAudioEngine — the self-contained Web Audio realizer of the cue spine.
 *
 * This is the FIRST consumer that wires the (previously unwired) engine spine
 * (`resolveCue` + `synthParams` + `voicePolicy` + the `Cue` model) to actual Web
 * Audio. It owns a 5-bus gain graph and accepts a shared `AudioContextOwner`;
 * standalone debug harnesses get an isolated owner, while gameplay shares one
 * browser device with the WASM wrapper and zone-music graph.
 *
 * It is the go-forward synth impl behind `IAudioEngine` — the wasm engine (dj-daw
 * Arc-6) later mirrors this same interface, at which point the factory swaps impls
 * and this class stays as the synth fallback. Not throwaway.
 *
 * Guardrails honoured:
 *  - Realizes synth-capable descriptors (tone / noise / sequence), reusing the
 *    legacy grain math in `synthParams.toneToParams`; sampled descriptors never
 *    enter the synth-only `scheduleGrain` path.
 *  - 5-bus graph matches `BusName` exactly. A per-voice `StereoPannerNode` applies
 *    `fire(cueId, { pan, gain })` (Option B: H2000 computes pan in TS, engine applies).
 *  - Simple master gain + mute (dev harness); NO UserPrefs gating.
 */

import type { IAudioEngine, CueLogEntry } from './IAudioEngine';
import type { BusName, Cue, SoundDesc, ToneDesc, NoiseDesc } from './audioTypes';
import { BUS_NAMES } from './audioTypes';
import { resolveCue } from '../cues/SoundBank';
import { toneToParams } from './synthParams';
import { shouldPlay, selectVoiceToSteal, type VoiceInfo } from './voicePolicy';
import { AudioContextOwner } from './AudioContextOwner';

const CUE_LOG_CAP = 64;
const MAX_VOICES = 24; // total concurrent voices across all cues (harness pool)

export class SynthAudioEngine implements IAudioEngine {
  private readonly contextOwner: AudioContextOwner;
  private readonly ownsContextOwner: boolean;
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private busGains: Partial<Record<BusName, GainNode>> = {};

  /** Requested bus gains (0..1), remembered even before the ctx exists. */
  private busGainValues: Record<BusName, number> = {
    sfx: 1, ui: 1, ambient: 1, music: 1, spatial: 1,
  };
  private masterVolume = 1;
  private muted = false;

  // Voice bookkeeping for the policy layer.
  private nextVoiceId = 1;
  /** Logical fire counter — grains of one fire share it (play-based maxStack). */
  private nextFireId = 1;
  private active: VoiceInfo[] = [];
  private activeNodes = new Map<number, { osc?: OscillatorNode; src?: AudioBufferSourceNode }>();
  private lastFiredAt = new Map<string, number>();

  private cueLog: CueLogEntry[] = [];

  constructor(contextOwner?: AudioContextOwner) {
    this.contextOwner = contextOwner ?? new AudioContextOwner();
    this.ownsContextOwner = contextOwner === undefined;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /** Lazily create the shared AudioContext graph during resume/audition. */
  private ensureGraph(): AudioContext {
    if (this.ctx) return this.ctx;
    const ctx = this.contextOwner.getOrCreate();
    const master = ctx.createGain();
    master.gain.value = this.muted ? 0 : this.masterVolume;
    master.connect(ctx.destination);
    for (const bus of BUS_NAMES) {
      const g = ctx.createGain();
      g.gain.value = this.busGainValues[bus];
      g.connect(master);
      this.busGains[bus] = g;
    }
    this.ctx = ctx;
    this.masterGain = master;
    return ctx;
  }

  async resume(): Promise<void> {
    const ctx = await this.contextOwner.resume();
    this.ensureGraph();
    if (ctx.state !== 'running') await ctx.resume();
  }

  /** Debug-kit health probe (🔊 window ENGINE strip) — a suspended context
   *  or a muted master is invisible in the fire trace (fires record with
   *  normal gains) yet produces total silence; this makes it one read. */
  health(): { contextState: AudioContextState | 'none'; muted: boolean; masterVolume: number } {
    return {
      contextState: this.ctx?.state ?? 'none',
      muted: this.muted,
      masterVolume: this.masterVolume,
    };
  }

  dispose(): void {
    for (const { osc, src } of this.activeNodes.values()) {
      try { osc?.stop(); } catch { /* already stopped */ }
      try { src?.stop(); } catch { /* already stopped */ }
    }
    this.activeNodes.clear();
    this.active = [];
    if (this.ctx) {
      this.masterGain?.disconnect?.();
      this.ctx = null;
      this.masterGain = null;
      this.busGains = {};
    }
    if (this.ownsContextOwner) void this.contextOwner.close();
  }

  // ── Mixer ──────────────────────────────────────────────────────────────────

  setBusGain(bus: BusName, gain: number): void {
    const g = clamp01(gain);
    this.busGainValues[bus] = g;
    const node = this.busGains[bus];
    if (node && this.ctx) node.gain.setValueAtTime(g, this.ctx.currentTime);
  }

  /** Dev-harness master control (not UserPrefs). */
  setMasterVolume(volume: number): void {
    this.masterVolume = clamp01(volume);
    this.applyMaster();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyMaster();
  }

  private applyMaster(): void {
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setValueAtTime(this.muted ? 0 : this.masterVolume, this.ctx.currentTime);
    }
  }

  setListener(_x: number, _y: number): void {
    void _x;
    void _y;
    // Reserved no-op placeholder (future 3D listener). Spatial pan is applied
    // per-voice via fire(cueId, { pan }) — Option B, H2000 owns the math in TS.
  }

  // ── Firing ─────────────────────────────────────────────────────────────────

  fire(cueId: string, opts?: { pan?: number; gain?: number; emitterId?: string }): void {
    const cue = resolveCue(cueId);
    if (!cue) return; // unknown id — no-op

    const ctx = this.ensureGraph();
    const now = ctx.currentTime * 1000; // ms, monotonic within the audio clock

    // Self-heal: retire voices past their scheduled end whose onended never
    // delivered (scheduled into a suspended context / throttled tab) —
    // zombie bookkeeping used to saturate maxStack cues into permanent
    // silence (2026-07-11 root find).
    if (this.active.some((v) => v.expectedEndAtMs !== undefined && v.expectedEndAtMs <= now)) {
      for (const v of this.active) {
        if (v.expectedEndAtMs !== undefined && v.expectedEndAtMs <= now) this.activeNodes.delete(v.id);
      }
      this.active = this.active.filter((v) => v.expectedEndAtMs === undefined || v.expectedEndAtMs > now);
    }

    // maxStack counts logical PLAYS (distinct fireIds), not grains — a
    // multi-grain sequence must not saturate its own stack on one fire.
    const activeCountOfCue = new Set(
      this.active.filter((v) => v.cueId === cueId).map((v) => v.fireId ?? v.id),
    ).size;
    if (!shouldPlay(cue, now, this.lastFiredAt.get(cueId), activeCountOfCue)) return;

    // Voice stealing when the shared pool is full.
    let stolen = false;
    if (this.active.length >= MAX_VOICES) {
      const victimId = selectVoiceToSteal(this.active, cue.priority);
      if (victimId == null) return; // nothing stealable → drop the incoming sound
      this.stopVoice(victimId);
      stolen = true;
    }

    this.lastFiredAt.set(cueId, now);

    const pan = clampPan(opts?.pan ?? 0);
    const gain = clamp01(opts?.gain ?? 1);
    const sound = pickVariant(cue.sound);
    const jitterSemis = cue.pitchJitter ? (Math.random() * 2 - 1) * cue.pitchJitter : 0;

    this.realize(ctx, cue, sound, gain, pan, jitterSemis, this.nextFireId++);

    this.cueLog.push({ t: Date.now(), cueId, bus: cue.bus, gain, pan, priority: cue.priority, stolen });
    if (this.cueLog.length > CUE_LOG_CAP) this.cueLog.shift();
  }

  drainCueLog(): CueLogEntry[] {
    const out = this.cueLog;
    this.cueLog = [];
    return out;
  }

  // ── Realization (tone / noise / sequence) ────────────────────────────────────

  private realize(
    ctx: AudioContext, cue: Cue, sound: SoundDesc,
    gainScale: number, pan: number, jitterSemis: number, fireId: number,
  ): void {
    if (sound.kind === 'sequence') {
      for (const step of sound.steps) {
        this.scheduleGrain(ctx, cue, step.desc, gainScale, pan, jitterSemis, step.delayMs / 1000, fireId);
      }
    } else if (sound.kind !== 'sample') {
      this.scheduleGrain(ctx, cue, sound, gainScale, pan, jitterSemis, 0, fireId);
    }
  }

  private scheduleGrain(
    ctx: AudioContext, cue: Cue, desc: ToneDesc | NoiseDesc,
    gainScale: number, pan: number, jitterSemis: number, offsetSec: number, fireId: number,
  ): void {
    const startAt = ctx.currentTime + offsetSec;
    const busNode = this.busGains[cue.bus] ?? this.busGains.sfx!;

    // Per-voice chain: source → voiceGain → panner → bus gain.
    const voiceGain = ctx.createGain();
    const panner = ctx.createStereoPanner();
    panner.pan.setValueAtTime(pan, startAt);
    voiceGain.connect(panner);
    panner.connect(busNode);

    const voiceId = this.nextVoiceId++;
    // expectedEndAtMs: scheduled end + a small pad — the self-heal deadline
    // when onended never delivers (see fire()'s zombie prune).
    const durationSec = desc.durationMs / 1000;
    const info: VoiceInfo = {
      id: voiceId,
      cueId: cue.id,
      priority: cue.priority,
      startedAt: startAt * 1000,
      fireId,
      expectedEndAtMs: (startAt + durationSec) * 1000 + 50,
    };
    this.active.push(info);

    if (desc.kind === 'tone') {
      const p = toneToParams(desc, gainScale, jitterSemis);
      const osc = ctx.createOscillator();
      osc.type = p.osc;
      osc.frequency.setValueAtTime(p.freq, startAt);
      osc.frequency.exponentialRampToValueAtTime(Math.max(p.sweepTo, 1), startAt + p.durationSec);
      voiceGain.gain.setValueAtTime(Math.max(p.peakGain, 0.0001), startAt);
      voiceGain.gain.exponentialRampToValueAtTime(p.endGain, startAt + p.durationSec);
      osc.connect(voiceGain);
      osc.start(startAt);
      osc.stop(startAt + p.durationSec);
      this.activeNodes.set(voiceId, { osc });
      osc.onended = () => this.retireVoice(voiceId);
    } else {
      // noise
      const durationSec = desc.durationMs / 1000;
      const src = ctx.createBufferSource();
      src.buffer = makeNoiseBuffer(ctx, durationSec);
      let tail: AudioNode = src;
      if (desc.filter) {
        const biquad = ctx.createBiquadFilter();
        biquad.type = desc.filter.type;
        biquad.frequency.setValueAtTime(desc.filter.freq, startAt);
        src.connect(biquad);
        tail = biquad;
      }
      const peak = Math.max((desc.gain ?? 1) * gainScale * 0.15, 0.0001);
      voiceGain.gain.setValueAtTime(peak, startAt);
      voiceGain.gain.exponentialRampToValueAtTime(0.001, startAt + durationSec);
      tail.connect(voiceGain);
      src.start(startAt);
      src.stop(startAt + durationSec);
      this.activeNodes.set(voiceId, { src });
      src.onended = () => this.retireVoice(voiceId);
    }
  }

  private retireVoice(voiceId: number): void {
    this.active = this.active.filter((v) => v.id !== voiceId);
    this.activeNodes.delete(voiceId);
  }

  private stopVoice(voiceId: number): void {
    const nodes = this.activeNodes.get(voiceId);
    if (nodes) {
      try { nodes.osc?.stop(); } catch { /* already stopped */ }
      try { nodes.src?.stop(); } catch { /* already stopped */ }
    }
    this.retireVoice(voiceId);
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clampPan(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

/** A Cue's `sound` may be a variation set (array) — pick one at random. */
function pickVariant(sound: Cue['sound']): SoundDesc {
  return Array.isArray(sound) ? sound[Math.floor(Math.random() * sound.length)] : sound;
}

function makeNoiseBuffer(ctx: AudioContext, durationSec: number): AudioBuffer {
  const frames = Math.max(1, Math.floor(ctx.sampleRate * durationSec));
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}
